import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from '@cotyorg/ccx-atoms/Link';
import Button from '@cotyorg/ccx-atoms/Button';
import Paragraph from '@cotyorg/ccx-atoms/Paragraph';
import Heading from '@cotyorg/ccx-atoms/Heading';
import ProductRating from '@cotyorg/ccx-molecules/ProductRating';
import { ProductSwatchColourLinks } from '@cotyorg/ccx-organism-product-swatch';
import R from 'ramda';
import { getGridColumnsTotal } from '../helpers';

const generateCardoContentClassNames = ({
  imageColumns,
  contentColumns,
  contentOverlay,
  contentTheme,
}) =>
  classNames('cardo__content', {
    'cardo__content--horizontal':
      imageColumns + contentColumns <= getGridColumnsTotal(),
    'cardo__content--cover-image': R.prop('coverImage', contentOverlay),
    'cardo__content--solid': contentTheme === 'solid',
    'cardo__content--alpha': contentTheme === 'alpha',
    'cardo__content--transparent': contentTheme === 'transparent',
  });

const CardoContent = ({
  tag,
  tagHeadingLevel,
  isTagUnderlined,
  description,
  linkUrl,
  linkText,
  linkOpenInNewWindow,
  title,
  headingLevel,
  isTitleUnderlined,
  averageRating,
  numberOfReviews,
  imgRatingOnSrc,
  imgRatingOffSrc,
  ratingIconWidth,
  ratingIconHeight,
  showStarRating,
  swatches,
  swatchLimit,
  moreVariantsIconSrc,
  imageColumns,
  contentColumns,
  contentOverlay,
  contentTheme,
  hideDescription,
  onClick,
  index,
  originUrl,
  openLinkInNewTab,
  fullClickableArea,
  renderCustomRating,
  bvid,
  additionalOnClick,
  additionalButtonText,
}) => (
  <div
    className={generateCardoContentClassNames({
      imageColumns,
      contentColumns,
      contentOverlay,
      contentTheme,
    })}
  >
    {tag && (
      <Heading
        level={tagHeadingLevel}
        isUnderlined={isTagUnderlined}
        extraClass="cardo__tag"
      >
        {tag}
      </Heading>
    )}

    {title && (
      <Heading
        level={headingLevel}
        isUnderlined={isTitleUnderlined}
        extraClass="cardo__heading"
        dangerouslySetInnerHTML={{ __html: title }}
      />
    )}

    {description && !hideDescription && (
      <Paragraph extraClass="cardo__description">{description}</Paragraph>
    )}

    {linkText && onClick && (
      <Paragraph extraClass="cardo__link-text" enableBBCode={false}>
        <Button
          testAttribute="cardo-link-text"
          extraClass="cardo__button"
          onClick={() => !fullClickableArea && onClick({ cardoIndex: index })}
        >
          {linkText}
        </Button>
      </Paragraph>
    )}

    {linkText && !onClick && fullClickableArea ? (
      <Paragraph extraClass="cardo__link-text" enableBBCode={false}>
        <span className="link cardo__link" data-test="cardo-link">
          {linkText}
        </span>
      </Paragraph>
    ) : linkText && !onClick && !fullClickableArea ? (
      <Paragraph extraClass="cardo__link-text" enableBBCode={false}>
        <Link
          testAttribute="cardo-link-text"
          extraClass="cardo__link"
          link={linkUrl}
          {...((openLinkInNewTab || linkOpenInNewWindow) && {
            target: '_blank',
          })}
        >
          {linkText}
        </Link>
      </Paragraph>
    ) : null}

    {!renderCustomRating && showStarRating ? (
      <div
        data-bv-show="inline_rating"
        data-bv-product-id={bvid}
        data-bv-seo
      />
    ) : typeof renderCustomRating === 'function' ? (
      renderCustomRating({ cardoIndex: index })
    ) : (
      renderCustomRating && (
        <ProductRating
          averageRating={averageRating}
          numberOfReviews={numberOfReviews}
          imgRatingOnSrc={imgRatingOnSrc}
          imgRatingOffSrc={imgRatingOffSrc}
          ratingIconWidth={ratingIconWidth}
          ratingIconHeight={ratingIconHeight}
        />
      )
    )}

    {additionalButtonText && additionalOnClick && (
      <Button
        testAttribute="cardo-additional-button-text"
        extraClass="cardo__additional-button"
        onClick={(e) => {
          e.preventDefault();

          additionalOnClick({ cardoIndex: index });
        }}
      >
        {additionalButtonText}
      </Button>
    )}

    {swatches && R.length(swatches) > 1 && (
      <ProductSwatchColourLinks
        linkUrl={linkUrl}
        swatches={swatches}
        swatchLimit={swatchLimit}
        moreIconSrc={moreVariantsIconSrc}
        originUrl={originUrl}
      />
    )}
  </div>
);

CardoContent.propTypes = {
  tag: PropTypes.string,
  tagHeadingLevel: PropTypes.number,
  isTagUnderlined: PropTypes.bool,
  description: PropTypes.string,
  linkUrl: PropTypes.string,
  linkText: PropTypes.string,
  linkOpenInNewWindow: PropTypes.bool,
  title: PropTypes.string,
  headingLevel: PropTypes.number,
  isTitleUnderlined: PropTypes.bool,
  averageRating: PropTypes.number,
  numberOfReviews: PropTypes.number,
  imgRatingOnSrc: PropTypes.string,
  imgRatingOffSrc: PropTypes.string,
  showStarRating: PropTypes.bool,
  swatches: PropTypes.arrayOf(PropTypes.object),
  swatchLimit: PropTypes.number,
  moreVariantsIconSrc: PropTypes.string,
  imageColumns: PropTypes.number.isRequired,
  contentColumns: PropTypes.number.isRequired,
  contentOverlay: PropTypes.shape({
    coverImage: PropTypes.bool.isRequired,
    verticalOffsetType: PropTypes.string.isRequired,
    horizontalOffset: PropTypes.bool.isRequired, // TODO: [1] not currently used. Check if needed for cards or just hero
  }),
  contentTheme: PropTypes.oneOf(['solid', 'alpha', 'transparent']).isRequired,
  hideDescription: PropTypes.bool,
  onClick: PropTypes.func,
  index: PropTypes.number,
  originUrl: PropTypes.string,
  openLinkInNewTab: PropTypes.bool,
  fullClickableArea: PropTypes.bool,
  renderCustomRating: PropTypes.func,
  ratingIconWidth: PropTypes.number,
  ratingIconHeight: PropTypes.number,
  bvid: PropTypes.string,
};

export default CardoContent;
